import { useEffect, useState } from "react";
import Header from "@/components/Header";
import InsightsCard from "@/components/InsightsCard";
import LymphieChat from "@/components/LymphieChat";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";
import { format, parseISO } from "date-fns";

export default function Trends() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/trends?days=30")
      .then((res) => setData(res.data.map((d) => ({ ...d, label: format(parseISO(d.date), "MMM d") }))))
      .finally(() => setLoading(false));
  }, []);

  const flareCount = data.filter((d) => d.flare_up).length;
  const avgPain = data.length ? (data.reduce((s, d) => s + d.pain_level, 0) / data.length).toFixed(1) : "0.0";
  const avgSwelling = data.length ? (data.reduce((s, d) => s + d.swelling_level, 0) / data.length).toFixed(1) : "0.0";
  const avgMood = data.length ? (data.reduce((s, d) => s + d.mood_intensity, 0) / data.length).toFixed(1) : "0.0";

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <p className="text-xs uppercase tracking-[0.25em] text-kawaii-textSoft font-semibold">Last 30 days</p>
        <h1 className="font-heading text-4xl md:text-5xl font-semibold text-kawaii-text mt-1 mb-8">Your emotional progress</h1>

        <InsightsCard />

        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Avg mood", value: `${avgMood}/5`, emoji: "💗", tint: "bg-kawaii-pink/20" },
            { label: "Avg pain", value: `${avgPain}/10`, emoji: "🌡️", tint: "bg-kawaii-peach/30" },
            { label: "Avg swelling", value: `${avgSwelling}/10`, emoji: "💧", tint: "bg-kawaii-lavender/30" },
            { label: "Flare-ups", value: flareCount, emoji: "🌸", tint: "bg-kawaii-mint/40" },
          ].map((s) => (
            <div key={s.label} className="kawaii-card p-5 flex items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl ${s.tint} flex items-center justify-center text-2xl`}>{s.emoji}</div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft">{s.label}</div>
                <div className="font-heading text-2xl font-semibold text-kawaii-text" data-testid={`trend-${s.label.replace(/\s/g,'-').toLowerCase()}`}>{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="kawaii-card p-8 text-center text-kawaii-textSoft">Gathering your gentle data...</div>
        ) : data.length === 0 ? (
          <div className="kawaii-card p-12 text-center">
            <div className="text-5xl mb-3">🌷</div>
            <h3 className="font-heading text-2xl text-kawaii-text mb-2">No entries yet</h3>
            <p className="text-kawaii-textSoft font-body">Start logging your days on the Calendar to see beautiful trends here.</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="kawaii-card p-6">
              <h3 className="font-heading text-lg font-semibold text-kawaii-text mb-4">Mood over time</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data}>
                    <CartesianGrid stroke="#EDE2F4" strokeDasharray="3 3" />
                    <XAxis dataKey="label" stroke="#7A6689" fontSize={11} />
                    <YAxis stroke="#7A6689" fontSize={11} domain={[0, 5]} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EDE2F4", borderRadius: 16 }} />
                    <Line type="monotone" dataKey="mood_intensity" stroke="#C8A2E0" strokeWidth={3} dot={{ r: 4, fill: "#C8A2E0" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="kawaii-card p-6">
              <h3 className="font-heading text-lg font-semibold text-kawaii-text mb-4">Pain & swelling</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data}>
                    <CartesianGrid stroke="#EDE2F4" strokeDasharray="3 3" />
                    <XAxis dataKey="label" stroke="#7A6689" fontSize={11} />
                    <YAxis stroke="#7A6689" fontSize={11} domain={[0, 10]} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EDE2F4", borderRadius: 16 }} />
                    <Legend />
                    <Line type="monotone" dataKey="pain_level" name="Pain" stroke="#F5D5C8" strokeWidth={3} dot={{ r: 4, fill: "#F5D5C8" }} />
                    <Line type="monotone" dataKey="swelling_level" name="Swelling" stroke="#B39BD8" strokeWidth={3} dot={{ r: 4, fill: "#B39BD8" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </main>
      <LymphieChat />
    </div>
  );
}
